<template>
  <form @submit.prevent="save" novalidate autocomplete="off">
    <div class="modal-card" style="width:840px; max-width:100%">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ isNew ? 'Add Note' : 'Edit Note' }} &mdash; {{ retailer.name }}</p>
        <b-tag v-if="!isNew" rounded :type="form.status === 'live' ? 'is-success' : 'is-warning'">
          {{ form.status === 'live' ? 'Live' : 'Draft' }}
        </b-tag>
      </header>
      <section class="modal-card-body">
        <b-message v-if="nonFieldErrors.length" type="is-danger" size="is-small" class="mb-md">
          <p v-for="(message, index) in nonFieldErrors" :key="index">{{ message }}</p>
        </b-message>
        <div class="columns">
          <!-- Topic -->
          <div class="column is-one-third">
            <b-field label="Topic" :type="fieldType('topic')" :message="fieldMessage('topic') || 'e.g. Invoicing, Shipping, Packaging.'">
              <b-input v-model="form.topic" placeholder="Invoicing" maxlength="64" />
            </b-field>
          </div>
          <!-- Title -->
          <div class="column">
            <b-field label="Title" :type="fieldType('title')" :message="fieldMessage('title')">
              <b-input v-model="form.title" placeholder="e.g. Email invoices — do not send EDI" maxlength="256" />
            </b-field>
          </div>
        </div>
        <!-- Note -->
        <b-field label="Note" :type="fieldType('note_html')" :message="fieldMessage('note_html')">
          <AnswerEditor v-model="form.note_html" />
        </b-field>
        <!-- Status -->
        <div class="has-background-white-ter rounded-lg pa-md">
          <b-switch v-model="isLive" type="is-success">
            <span class="has-text-weight-bold">{{ isLive ? 'Live' : 'Draft' }}</span>
          </b-switch>
          <p class="is-size-7 has-text-grey mt-xs">
            Notes are an internal reference for staff either way; Live marks the
            note as vetted (and ready if notes are ever surfaced to suppliers).
          </p>
        </div>
      </section>
      <footer class="modal-card-foot" style="justify-content:space-between">
        <div>
          <ConfirmButton
            v-if="!isNew"
            initialLabel="Delete"
            type="is-danger"
            :loading="deleting"
            :disabled="saving"
            @action:confirmed="remove" />
        </div>
        <div class="buttons is-marginless is-vcentered">
          <span v-if="!isValid" class="is-size-7 has-text-grey mr-md">
            Add {{ missingFields.join(', ') }} to save.
          </span>
          <button class="button" type="button" @click="$parent.close()">Close</button>
          <b-button
            native-type="submit"
            type="is-primary"
            :loading="saving"
            :disabled="!isValid || saving || deleting">
            {{ isNew ? 'Add Note' : 'Save Changes' }}
          </b-button>
        </div>
      </footer>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import AnswerEditor from './AnswerEditor'
import ConfirmButton from '@/components/ConfirmButton'

const stripTags = (html) => String(html || '').replace(/<[^>]*>/g, '').trim()

export default {
  components: {
    AnswerEditor,
    ConfirmButton
  },
  computed: {
    ...mapGetters(['api']),
    isNew () {
      return this.note === null
    },
    endpoint () {
      let route = this.api.baseUrl + '/v1/retailers/' + this.retailer.id + '/notes/'
      if (!this.isNew) {
        route += this.note.id + '/'
      }
      return route
    },
    method () {
      return this.isNew ? 'post' : 'patch'
    },
    json () {
      return {
        topic: this.form.topic.trim(),
        title: this.form.title.trim(),
        note_html: this.form.note_html,
        status: this.form.status
      }
    },
    isLive: {
      get () {
        return this.form.status === 'live'
      },
      set (value) {
        this.form.status = value ? 'live' : 'draft'
      }
    },
    missingFields () {
      const missing = []
      if (this.form.topic.trim() === '') missing.push('a topic')
      if (this.form.title.trim() === '') missing.push('a title')
      if (stripTags(this.form.note_html) === '') missing.push('a note')
      return missing
    },
    isValid () {
      return this.missingFields.length === 0
    },
    nonFieldErrors () {
      const messages = []
      const collect = (value) => {
        if (Array.isArray(value)) {
          value.forEach(collect)
        } else if (typeof value === 'string') {
          messages.push(value)
        }
      }
      collect(this.errors.non_field_errors)
      collect(this.errors.detail)
      return messages
    }
  },
  data () {
    // List rows carry the full note (incl. note_html), so edit populates
    // synchronously — no fetch, and the editor mounts exactly once.
    const note = this.note
    return {
      saving: false,
      deleting: false,
      errors: {},
      form: {
        topic: note ? note.topic : '',
        title: note ? note.title : '',
        note_html: note ? note.note_html : '',
        status: note ? note.status : 'draft'
      }
    }
  },
  methods: {
    fieldType (field) {
      return (field in this.errors) ? 'is-danger' : ''
    },
    fieldMessage (field) {
      if (!(field in this.errors)) return ''
      const value = this.errors[field]
      return Array.isArray(value) ? value.join(' ') : String(value)
    },
    save () {
      if (!this.isValid) return
      this.saving = true
      this.errors = {}
      this.$http[this.method](this.endpoint, this.json).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: this.isNew ? 'Note added!' : 'Note saved!' })
        this.$emit('updated')
        this.$parent.close()
      }).catch(error => {
        if (error.response && error.response.status === 400) {
          this.errors = error.response.data
        }
        this.$buefy.toast.open({ type: 'is-danger', message: 'Please review your input and try again.' })
      }).finally(() => {
        this.saving = false
      })
    },
    remove () {
      this.deleting = true
      this.$http.delete(this.endpoint).then(() => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Note deleted.' })
        this.$emit('deleted')
        this.$parent.close()
      }).catch(() => {
        this.$buefy.toast.open({ type: 'is-danger', message: 'The note could not be deleted.' })
      }).finally(() => {
        this.deleting = false
      })
    }
  },
  props: {
    retailer: {
      type: Object,
      required: true
    },
    // null to create; a note row to edit.
    note: {
      type: Object,
      default: null
    }
  }
}
</script>
